import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const accounts = {
    layout: {
        listTitle: 'Accounts',
        archivedTitle: 'Archived accounts',
        newAccountTitle: 'New Account',
        editAccountTitle: 'Edit Account',
        reportTitle: 'Account report',
    },
    types: {
        checking: 'Checking Account',
        savings: 'Savings',
        wallet: 'Wallet',
        digital: 'Digital Account',
        investment: 'Investment',
        other: 'Other',
    },
    list: {
        transferAction: 'Transfer',
        transferActionAccessibility: 'Transfer from {{name}}',
        archiveAction: 'Archive',
        archiveActionAccessibility: 'Archive {{name}}',
        archivedRowLabel: 'Archived accounts',
        archivedRowAccessibility_one: '1 archived account. Open list',
        archivedRowAccessibility_other: '{{count}} archived accounts. Open list',
        excludedBadge: 'Excluded',
        reservedLabel: 'Reserved',
        submersaWarning: 'Pockets exceed your balance — add the missing deposit or withdraw from a pocket',
        availableBalanceLabel: 'Available balance',
        totalBalanceLabel: 'Total balance',
        deltaThisMonth: '{{amount}} this month',
        accountCount_one: '{{count}} account',
        accountCount_other: '{{count}} accounts',
        emptyTitle: 'No accounts',
        emptySubtitleWithArchived: 'Your archived accounts are just below, under Archived accounts.',
        emptySubtitle: 'Add your first account to get started',
        emptyAction: 'Add account',
    },
    archived: {
        reactivateButton: 'Reactivate',
        emptyTitle: 'No archived accounts',
        emptySubtitle: 'Archived accounts appear here for you to reactivate whenever you want.',
    },
    form: {
        typeChecking: 'Checking',
        typeSavings: 'Savings',
        typeWallet: 'Wallet',
        typeDigital: 'Digital',
        typeInvestment: 'Investment',
        typeOther: 'Other',
        nameLabel: 'Account name',
        namePlaceholder: 'e.g. Chase, Wallet',
        typeSectionLabel: 'Account type',
        institutionLabel: 'Institution / Bank',
        institutionPlaceholder: 'Institution name (optional)',
        colorLabel: 'Color',
        initialBalanceLabel: 'Initial balance',
        excludeFromTotalsLabel: 'Exclude from total balance',
        excludeFromTotalsDescription: 'Keeps the account visible and available for transfers, but excludes this balance from totals, net worth, and projections.',
        unarchiveAction: 'Unarchive account',
        archiveAction: 'Archive account',
        archiveDescription: 'Hidden from the list and Home. History and transfers are kept.',
        deleteAction: 'Delete account',
        createAccessibility: 'Create account',
        saveAccessibility: 'Save',
        colorSheetTitle: 'Choose a color',
    },
    archiveDialog: {
        unarchiveTitle: 'Unarchive account',
        unarchiveMessage: 'The account will appear again in lists and calculations.',
        unarchiveConfirm: 'Unarchive',
    },
    archiveToast: {
        title: 'Account archived',
        message: '{{name}} is now in Archived accounts.',
        undo: 'Undo',
    },
    archiveBalanceDialog: {
        title: 'Archive {{name}}?',
        message: 'This account still has {{amount}}. Once archived, it leaves your total balance and Home. History is kept and you can reactivate it anytime.',
        confirm: 'Archive',
    },
    archiveBlockedDialog: {
        title_one: 'This account has a pocket',
        title_other: 'This account has {{count}} pockets',
        message_one: 'To archive this account, withdraw the money or archive the pocket first, so nothing is left in a hidden account.',
        message_other: 'To archive this account, withdraw the money or archive the pockets first, so nothing is left in a hidden account.',
        viewPockets_one: 'View pocket',
        viewPockets_other: 'View pockets',
        cancel: 'Not now',
    },
    deleteDialog: {
        title: 'Delete account',
        message: 'Are you sure? All transactions in this account will be kept, but the account will be removed.',
        confirm: 'Delete',
    },
    detail: {
        typeChecking: 'Checking account',
        typeDigital: 'Digital account',
        typeFallback: 'Account',
        available: 'Available',
        balance: 'Balance',
        adjustBalanceAction: 'Adjust balance',
        pocketAction: 'Pocket',
        reportAction: 'Report',
        emptyTransactionsTitle: 'No transactions',
        emptyTransactionsSubtitle: 'No activity in {{month}}',
        searchPlaceholder: 'Search this statement...',
        emptySearchTitle: 'No results',
        emptySearchSubtitle: 'Nothing with that name or amount in {{month}}',
        registerTransferButton: 'Register transfer',
        adjustButton: 'Adjust',
        continueButton: 'Continue',
        adjustBackAccessibility: 'Back to the amount',
        adjustDateAccessibility: 'Adjustment date',
        adjustReasonPlaceholder: 'Reason (optional) — e.g. cash shortage',
        newAvailableBalanceLabel: 'Available balance (new)',
        newRealBalanceLabel: 'Actual balance (new)',
        adjustToday: 'Today: {{current}}',
        adjustTodayWithReserved: 'Today: {{current}} · Pockets: {{reserved}} (untouched)',
        differenceOfLabel: 'Difference of {{amount}}',
        adjustWhereToTitle: 'Where did {{amount}} go?',
        adjustWhereFromTitle: 'Where did {{amount}} come from?',
        wasTransferLabel: 'It was a transfer',
        transferFromDescription: 'The money came from another of your accounts',
        transferToDescription: 'The money went to another of your accounts',
        sourceAccountPlaceholder: 'Source account...',
        destinationAccountPlaceholder: 'Destination account...',
        natureExpenseTitle: 'Record as expense',
        natureIncomeTitle: 'Record as income',
        natureExpenseDescription: 'Money spent and never logged — counts in reports',
        natureIncomeDescription: 'Money received and never logged — counts in reports',
        natureNeutralTitle: 'Just adjust',
        natureNeutralDescription: 'Fixes the balance without touching reports',
        adjustCategoryPlaceholder: 'Category (optional)...',
        postExpenseButton: 'Record expense and adjust',
        postIncomeButton: 'Record income and adjust',
        adjustOnlyButton: 'Adjust balance',
        adjustmentExpenseDescription: 'Adjustment expense',
        adjustmentIncomeDescription: 'Adjustment income',
        pocketsTitle: 'Pockets',
        cxSheetReservedSuffix: ' · {{amount}} reserved',
        newPocketAccessibility: 'New pocket',
        pocketsEmpty: 'No pockets in this account yet. Tap + to create the first one.',
        pocketProgress: '{{percent}}% of {{amount}}',
        pocketNoGoal: 'no goal',
        adjustmentTitle: 'Balance adjustment',
        changeValueAction: 'Change amount',
        deleteAdjustmentTitle: 'Delete balance adjustment?',
        deleteAdjustmentMessage: "The account balance returns to what it was before this correction. Income and expenses for the month don't change.",
        editAccountAccessibility: 'Edit account',
        newTransactionAccessibility: 'New transaction in this account',
        sortAscendingAccessibility: 'Oldest first. Tap to reverse.',
        sortDescendingAccessibility: 'Newest first. Tap to reverse.',
    },
    report: {
        incomeLabel: 'Money in',
        expenseLabel: 'Money out',
        netLabel: 'Period balance',
        reservas: {
            title: 'Pockets',
            deposited: 'Saved',
            withdrawn: 'Withdrawn',
            yielded: 'Yield',
            reservedAtEnd: 'Reserved at month end',
            info: 'Saving and withdrawing are not income or expense: the money stays in the account and only Available changes. Yield also shows under Income.',
        },
        itemsLabel: 'Items',
        itemCount_one: '{{count}} entry',
        itemCount_other: '{{count}} entries',
        adjustmentsLabel: 'Balance adjustment',
        adjustmentsNote_one: '{{count}} balance adjustment in this period — it shows in the list but not in money in/out.',
        adjustmentsNote_other: '{{count}} balance adjustments in this period — they show in the list but not in money in/out.',
        byCategoryTitle: 'By category',
        byTagTitle: 'By tag',
        transfersLabel: 'Transfers',
        invoicesLabel: 'Card invoices',
        loansLabel: 'Receivables',
        uncategorized: 'No category',
        untagged: 'No tag',
        filterByCategory: 'Categories',
        allCategories: 'All categories',
        filterByTag: 'Tags',
        allTags: 'All tags',
        filterCategoryMulti: '{{n}} categories',
        filterTagMulti: '{{n}} tags',
        emptyFilter: 'No entries match this filter.',
        emptyMonth: 'No activity in {{month}}.',
        exportPdf: 'Export PDF',
        exportError: 'Export failed',
        pdfTitle: '{{name}} account',
        pdfSectionTitle: 'Entries',
        pdfColDate: 'Date',
        pdfColDescription: 'Description',
        pdfColCategory: 'Category',
        pdfColTag: 'Tag',
        pdfColItems: 'Items',
        pdfColIncome: 'Money in',
        pdfColExpense: 'Money out',
        pdfColAmount: 'Amount',
    },
} satisfies DeepPartialStrings<TranslationSchema['accounts']>;
